import { motion } from "framer-motion";
import type React from "react";
import { Link } from "@/i18n/navigation";
import { cn } from "@/lib/utils";

type BottomNavItemProps = {
  icon: React.ComponentType<{ variant: "Bold" | "Linear"; className?: string }>;
  label: string;
  isActive: boolean;
} & ({ href: string; onClick?: never } | { href?: never; onClick: () => void });

export const BottomNavItem: React.FC<BottomNavItemProps> = ({
  href,
  onClick,
  icon: Icon,
  label,
  isActive,
}) => {
  const className = cn(
    "flex min-w-0 flex-1 basis-0 flex-col items-center justify-center pb-[12px] font-medium",
    "group transition-all duration-500 ease-in-out motion-reduce:transition-none",
    isActive ? "text-accent" : "text-foreground",
  );
  // Keep the caption row at the same height for every item. The raised circle
  // changes its top position, while both active and inactive labels stay aligned.
  const content = (
    <>
      <motion.span
        className={cn(
          "flex size-[56px] items-center justify-center rounded-full bg-transparent",
          "transition-all duration-500 ease-in-out motion-reduce:transition-none",
          isActive ? "mb-[2px] bg-bg-secondary" : "-mb-[24px]",
        )}
      >
        <Icon variant={isActive ? "Bold" : "Linear"} className="size-[24px]" />
      </motion.span>
      <span className="flex h-8 w-full items-center justify-center whitespace-normal break-words text-center text-[11px] leading-[14px]">
        {label}
      </span>
    </>
  );
  if (href === undefined) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-haspopup="dialog"
        aria-expanded={false}
        className={className}
      >
        {content}
      </button>
    );
  }
  return (
    <Link
      href={href}
      aria-current={isActive ? "page" : undefined}
      className={className}
    >
      {content}
    </Link>
  );
};
